"use client";

import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import {
  type ReferralResolution,
  ReferralSection,
} from "@/components/features/checkout";
import { Form, featureSchemas } from "@/components/forms";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { usePurchaseNFT } from "@/hooks/nft/usePurchaseNFT";
import { useNoReferrerGuard } from "@/hooks/useNoReferrerGuard";
import { useFetchContractUser } from "@/hooks/user/useFetchContractUser";
import { useRouter } from "@/i18n/navigation";
import type { components } from "@/types/api";
import { BlurImage } from "../BlurImage";
import { NFTCardSuccess } from "./NFTCardSuccess";

interface NFTCardPurchaseContentProps {
  nftData: components["schemas"]["AvailableNftResponseDto"];
  nftMarketAddress: string;
  nftCollectionAddress: string;
  isTicketEnabled?: boolean;
  marketTypeId?: string;
  onRedirect?: () => void;
}

export const NFTCardPurchaseContent = ({
  nftData,
  nftMarketAddress,
  nftCollectionAddress,
  isTicketEnabled,
  marketTypeId,
  onRedirect,
}: NFTCardPurchaseContentProps) => {
  const t = useTranslations("nft");
  const router = useRouter();
  const params = useParams();
  const { isLoading } = useFetchContractUser();
  const [showSuccess, setShowSuccess] = useState(false);
  const [purchasedTokenId, setPurchasedTokenId] = useState<number | null>(null);
  const { confirmReferrer, NoReferrerGuardModal } = useNoReferrerGuard();

  // Placement resolution is owned by <ReferralSection> (chain-derived, keyed
  // on the CONNECTED wallet). Only one of the two forms below renders at a
  // time (gated by isTicketEnabled), so a single shared slot is safe.
  const [referral, setReferral] = useState<ReferralResolution>({
    canPurchase: false,
    referrerAddress: undefined,
    parentAddress: undefined,
    mode: "loading",
    willLockPlacement: false,
    showNoReferrerWarning: false,
    blockedMessageKey: undefined,
  });

  const purchaseMutation = usePurchaseNFT({
    onSuccess: (data) => {
      setPurchasedTokenId(data.tokenId);
      setShowSuccess(true);
    },
  });

  const price = useMemo(
    () => (nftData?.marketType?.price ? nftData?.marketType?.price : null),
    [nftData],
  );

  const handlePurchase = async () => {
    if (!price) return;

    // Either the buyer is already on-chain (arguments ignored by the
    // contract), the chain confirmed these addresses are usable, or this is
    // a deliberate no-referrer election (zero address). No fallback.
    const { canPurchase, referrerAddress, parentAddress, willLockPlacement } =
      referral;
    if (!canPurchase || !referrerAddress || !parentAddress) return;

    // This purchase permanently fixes a placement decision — confirm it
    // before minting.
    if (!(await confirmReferrer(willLockPlacement))) return;

    const tokenId = nftData.token_id;

    await purchaseMutation.mutateAsync({
      tokenId,
      parentAddress: parentAddress as `0x${string}`,
      referrerAddress: referrerAddress as `0x${string}`,
      price: {
        sv: Number(price?.sv),
        bv: Number(price?.bv),
      },
      nftMarketAddress,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Avatar className="size-10 rounded-sm border border-stroke-light">
          <div className="w-full h-full overflow-hidden">
            <BlurImage
              src={nftData.imageUrl ?? "/images/ui/nft-placeholder.svg"}
              alt="NFT Card"
              wrapperClassName="object-cover w-full h-full"
              // Aryosense-only land zoom-crop (other projects render the image as-is)
              // arusense.com died; images moved to our own MinIO (aryosense-lands prefix).
              className={`object-cover w-full h-full${nftData.imageUrl?.includes("arusense.com") || nftData.imageUrl?.includes("aryosense-lands") ? " object-[50%_6%] scale-150" : ""}`}
              width={40}
              height={40}
              loading="lazy"
            />
          </div>
        </Avatar>
        <div className="flex items-center justify-between font-semibold text-sm text-text-primary w-full">
          <p>
            {t("purchase.planLabel", { plan: String(nftData.marketType.plan) })}
          </p>
          <p dir="ltr">
            {price?.sv} USDT + {price?.bv} UVM
          </p>
        </div>
      </div>

      <Separator className="my-4 bg-stroke-subtle" />

      {isTicketEnabled ? (
        <Form
          schema={featureSchemas.fastCheckout.fastCheckoutForm}
          onSubmit={(data) => {
            const projectId = params.slot;
            const ticketUrl = new URL(
              `/fast-checkout/${projectId}/ticket`,
              window.location.origin,
            );
            ticketUrl.searchParams.set("marketTypeId", marketTypeId ?? "");
            ticketUrl.searchParams.set("tokenId", String(nftData.token_id));
            // Carry both fields through unconditionally — even blank is a
            // real value (zero address / not-yet-eligible). The ticket page
            // re-resolves and re-validates both live against the chain
            // before submitting; this is only a convenience carry-over, not
            // a trusted source of truth.
            if (data.referrerAddress) {
              ticketUrl.searchParams.set(
                "referrerAddress",
                data.referrerAddress,
              );
            }
            if (data.parentAddress) {
              ticketUrl.searchParams.set("parentAddress", data.parentAddress);
            }
            router.push(ticketUrl.pathname + ticketUrl.search);
            onRedirect?.();
          }}
          defaultValues={{
            referrerAddress: undefined,
            parentAddress: undefined,
          }}
        >
          {() => (
            <>
              <ReferralSection
                referrerFieldName="referrerAddress"
                parentFieldName="parentAddress"
                cartGrossBv={Number(price?.bv) || 0}
                onResolutionChange={setReferral}
              />
              <Button
                type="submit"
                colors={"primary"}
                radius={"sm"}
                disabled={!referral.canPurchase}
              >
                {t("purchase.continue")}
              </Button>
            </>
          )}
        </Form>
      ) : isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <Form
          schema={featureSchemas.fastCheckout.fastCheckoutForm}
          onSubmit={handlePurchase}
          defaultValues={{
            referrerAddress: undefined,
            parentAddress: undefined,
          }}
        >
          {() => {
            return (
              <>
                <ReferralSection
                  referrerFieldName="referrerAddress"
                  parentFieldName="parentAddress"
                  cartGrossBv={Number(price?.bv) || 0}
                  onResolutionChange={setReferral}
                />
                <Button
                  type="submit"
                  colors={"primary"}
                  radius={"sm"}
                  loading={purchaseMutation.isPending}
                  disabled={!referral.canPurchase || purchaseMutation.isPending}
                >
                  {t("purchase.buy")}
                </Button>
              </>
            );
          }}
        </Form>
      )}

      {/* Irreversible-placement purchase confirmation */}
      <NoReferrerGuardModal />

      {/* Success Dialog */}
      <NFTCardSuccess
        open={showSuccess}
        tokenId={purchasedTokenId}
        nftCollectionAddress={nftCollectionAddress}
        onOpenChange={(open) => {
          setShowSuccess(open);
          if (!open) {
            purchaseMutation.reset();
            setPurchasedTokenId(null);
          }
        }}
      />
    </div>
  );
};
